import { getImageProps } from "next/image";

/** Content column inside `.tu-container` (1rem padding each side). */
export const SIZES_CONTAINER =
	"(max-width: 991px) calc(100vw - 2rem), min(640px, calc(100vw - 360px))";

/** Blog sidebar carousel (~346px mobile, ~320px desktop). */
export const SIZES_SIDEBAR =
	"(max-width: 991px) min(90vw, calc(100vw - 2rem)), 320px";

/** Full-bleed hero slideshow (slide art 1710×316; stage max-height 380px → ~2057px wide). */
export const SIZES_HERO =
	"(max-width: 991px) 100vw, min(2057px, 100vw)";

/** Services hero — mobile column vs desktop main column (16:9 box, max-height 420px → ~746px wide). */
export const SIZES_SERVICES_MOBILE = "calc(100vw - 2rem)";
export const SIZES_SERVICES_DESKTOP =
	"min(746px, calc(100vw - 360px))";

/** `.air-cooler-div` — JS height 30vw (≤1000px) or 339px; aspect-ratio ~1.009 → ~30.3vw / 342px wide. */
export const SIZES_AIR_COOLER =
	"(max-width: 1000px) 30.3vw, 342px";

/** `.fusion-cooler-div` — ≤1000px: 44.8vw wide; desktop: up to 28rem (448px) per product-detail CSS. */
export const SIZES_FUSION_COOLER =
	"(max-width: 1000px) 44.8vw, 448px";

/** Widths allowed in `next.config.js` `images.imageSizes` / `deviceSizes`. */
const ALLOWED = [320, 384, 440, 640, 750, 828, 880, 1080, 1200];

export function capImageUrl(src, width, quality) {
	const url = new URL(src, "http://n");
	url.searchParams.set("w", String(width));
	url.searchParams.set("q", String(quality));
	return `${url.pathname}${url.search}`;
}

/** Replace vague `100vw` / `90vw` with slot-accurate `sizes` values. */
export function normalizeSizes(sizes, { preserveRaw = false } = {}) {
	if (preserveRaw || !sizes) return sizes || SIZES_CONTAINER;
	if (sizes === "100vw") return SIZES_CONTAINER;
	if (sizes === "90vw") return SIZES_SIDEBAR;
	return sizes;
}

/**
 * Conservative slot width for srcset capping (mobile-first).
 * Avoids Math.max(960px) from desktop `sizes` leaking into mobile srcset.
 */
export function maxSlotFromSizes(sizes) {
	if (!sizes) return 440;
	if (sizes.includes("2057px") && sizes.includes("100vw")) return 1710;
	if (sizes.includes("1470px") && sizes.includes("100vw")) return 1470;
	if (sizes.includes("880px") && sizes.includes("100vw")) return 880;
	if (sizes.includes("1200px") && sizes.includes("100vw")) return 1200;
	if (sizes.includes("840px")) return 840;
	if (sizes.includes("746px")) return 746;
	if (sizes.includes("320px") && sizes.includes("90vw")) return 360;
	if (sizes.includes("400px")) return 400;
	if (sizes.includes("50vw")) return 480;
	if (sizes.includes("25vw")) return 300;
	if (sizes.includes("calc(100vw - 2rem)")) return 440;
	if (sizes.includes("90vw")) return 360;
	if (sizes.includes("100vw")) return 440;
	/** Cooler heroes — include desktop px slot, not only the mobile vw estimate. */
	if (sizes.includes("30.3vw") || sizes.includes("44.8vw")) {
		const px = [...sizes.matchAll(/(\d+)px/g)].map((m) => Number(m[1]));
		const mobile = sizes.includes("44.8vw") ? 200 : 130;
		return px.length ? Math.max(mobile, ...px) : mobile;
	}
	const px = [...sizes.matchAll(/(\d+)px/g)].map((m) => Number(m[1]));
	if (px.length) return Math.min(...px);
	return 440;
}

function nearestAllowed(target) {
	return ALLOWED.find((w) => w >= target) || ALLOWED[ALLOWED.length - 1];
}

/**
 * @param {'hero'|'wide'|'sidebar'|'content'} variant
 * hero — home carousel; wide — services hero; sidebar — blog carousel; content — default pages
 */
export function pickSrcsetWidths(maxSlot, variant = "content") {
	if (variant === "hero") {
		const top = nearestAllowed(Math.min(maxSlot, 1200));
		return [...new Set([640, 750, 828, 880, 1080, top].filter((w) => w <= top))].sort(
			(a, b) => a - b,
		);
	}
	if (variant === "wide") {
		return [440, 640];
	}
	if (maxSlot <= 200) {
		return [320];
	}
	if (variant === "sidebar" || maxSlot <= 360) {
		return [320, 384, 440];
	}
	/** Default pages — 640w srcset max keeps Lighthouse mobile from picking 828w+. */
	return [384, 440, 640];
}

function resolveVariant({ variant, maxSlotWidth, sizes, preserveSizes }) {
	if (variant) return variant;
	if (maxSlotWidth >= 1200 || (preserveSizes && sizes?.includes("1200px"))) {
		return "hero";
	}
	if (maxSlotWidth >= 800) return "wide";
	if (maxSlotWidth <= 360) return "sidebar";
	return "content";
}

/**
 * @param {object} opts
 * @param {'hero'|'wide'|'sidebar'|'content'} [opts.variant]
 */
export function getCappedImageProps({
	src,
	alt = "",
	width,
	height,
	fill = false,
	sizes,
	quality = 70,
	priority = false,
	maxSlotWidth,
	preserveSizes = false,
	variant,
	srcsetWidths: srcsetWidthsOverride,
}) {
	const normalizedSizes = normalizeSizes(sizes, { preserveRaw: preserveSizes });
	const slot = maxSlotWidth ?? maxSlotFromSizes(normalizedSizes);
	const resolvedVariant = resolveVariant({
		variant,
		maxSlotWidth: slot,
		sizes: normalizedSizes,
		preserveSizes,
	});
	const srcsetWidths =
		srcsetWidthsOverride ||
		pickSrcsetWidths(slot, resolvedVariant);
	const defaultWidth =
		srcsetWidths.find((w) => w >= slot) ||
		srcsetWidths[srcsetWidths.length - 1];

	const imageArgs = {
		src,
		alt,
		sizes: normalizedSizes,
		quality,
		...(priority ? { priority: true } : {}),
	};

	if (fill) {
		imageArgs.fill = true;
	} else {
		imageArgs.width = width || defaultWidth;
		imageArgs.height =
			height || Math.round((imageArgs.width * 9) / 16);
	}

	const { props } = getImageProps(imageArgs);
	const srcSet = srcsetWidths
		.map((w) => `${capImageUrl(props.src, w, quality)} ${w}w`)
		.join(", ");

	return {
		...props,
		src: capImageUrl(props.src, defaultWidth, quality),
		srcSet,
		sizes: normalizedSizes,
		loading: priority ? "eager" : props.loading || "lazy",
		fetchPriority: priority ? "high" : props.fetchPriority,
	};
}
